import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Container,
  Header,
  Wizard,
  SpaceBetween,
  FormField,
  Input,
  Box,
  Alert,
  Multiselect,
  DatePicker,
} from '@cloudscape-design/components';

const initialForm = {
  referencia: '',
  di: '',
  bu: '',
  poso: '',
  regime: '',
  modal: '',
  etd: '',
  eta: '',
  descricao: '',
  groups: []
};

export default function CriarProcesso() {
  const [form, setForm] = useState(initialForm);
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupsFilter, setGroupsFilter] = useState('');
  const navigate = useNavigate();

  const stage = process.env.REACT_APP_STAGE;

  // Fetch groups from Cognito
  useEffect(() => {
    async function fetchGroups() {
      setIsLoadingGroups(true);
      try {
        const accessToken = localStorage.getItem('access_token');
        
        // Call your API to get groups from Cognito
        const response = await axios.get('https://jbc540hz12.execute-api.us-east-2.amazonaws.com/groups', 
        {
          headers: {
            'Authorization': accessToken
          }
        });
        
        const groupsArray = response.data.groups || [];
        
        // Map string array to the format needed by Multiselect
        setAvailableGroups(groupsArray.map(group => ({
          value: group,
          label: group
        })));
      } catch (err) {
        console.error('Error fetching groups:', err);
        setError('Erro ao carregar grupos. Por favor, tente novamente.');
      } finally {
        setIsLoadingGroups(false);
      }
    }

    fetchGroups();
  }, []);

  // Step 1: Input Form
  const inputStep = (
    <SpaceBetween size="m">
      <FormField label="Referência" required>
        <Input value={form.referencia} onChange={e => setForm({ ...form, referencia: e.detail.value })} />
      </FormField>
      <FormField label="DI" required>
        <Input value={form.di} onChange={e => setForm({ ...form, di: e.detail.value })} />
      </FormField>
      <FormField label="BU" required>
        <Input value={form.bu} onChange={e => setForm({ ...form, bu: e.detail.value })} />
      </FormField>
      <FormField label="PO/SO" required>
        <Input value={form.poso} onChange={e => setForm({ ...form, poso: e.detail.value })} />
      </FormField>
      <FormField label="Regime" required>
        <Input value={form.regime} onChange={e => setForm({ ...form, regime: e.detail.value })} />
      </FormField>
      <FormField label="Modal" required>
        <Input value={form.modal} onChange={e => setForm({ ...form, modal: e.detail.value })} />
      </FormField>
      <FormField label="ETD" required>
        <DatePicker
          value={form.etd}
          onChange={({ detail }) => setForm({ ...form, etd: detail.value })}
          placeholder="YYYY-MM-DD"
          ariaLabel="Escolher data ETD"
          i18nStrings={{
            openCalendarAriaLabel: "Abrir calendário",
            todayAriaLabel: "Hoje"
          }}
        />
      </FormField>
      <FormField label="ETA" required>
        <DatePicker
          value={form.eta}
          onChange={({ detail }) => setForm({ ...form, eta: detail.value })}
          placeholder="YYYY-MM-DD"
          ariaLabel="Escolher data ETA"
          i18nStrings={{
            openCalendarAriaLabel: "Abrir calendário",
            todayAriaLabel: "Hoje"
          }}
        />
      </FormField>
      <FormField label="Descrição" required>
        <Input value={form.descricao} onChange={e => setForm({ ...form, descricao: e.detail.value })} />
      </FormField>
      <FormField label="Grupos de destino" required>
        <Multiselect
          selectedOptions={form.groups}
          onChange={({ detail }) => setForm({ ...form, groups: detail.selectedOptions })}
          options={availableGroups}
          filteringType="manual"
          filteringPlaceholder="Buscar grupos"
          statusType={isLoadingGroups ? "loading" : "finished"}
          loadingText="Carregando grupos..."
          placeholder="Selecione os grupos"
          filteringText={groupsFilter}
          onFilteringChange={({ detail }) => setGroupsFilter(detail.filteringText)}
          deselectAriaLabel={e => `Remover ${e.label}`}
          i18nStrings={{
            filteringAriaLabel: "Filtrar grupos",
            filteringPlaceholder: "Buscar grupos",
            selectedAriaLabel: "Selecionado",
            enteredTextLabel: text => `Use: "${text}"`,
            removeButtonAriaLabel: option => `Remover ${option.label}`
          }}
        />
      </FormField>
    </SpaceBetween>
  );

  // Step 2: Review
  const reviewStep = (
    <Box>
      <Header>Revisar Dados</Header>
      <SpaceBetween size="xs">
        <div><strong>Referência:</strong> {form.referencia}</div>
        <div><strong>DI:</strong> {form.referencia}</div>
        <div><strong>BU:</strong> {form.bu}</div>
        <div><strong>PO/SO:</strong> {form.poso}</div>
        <div><strong>Regime:</strong> {form.regime}</div>
        <div><strong>Modal:</strong> {form.modal}</div>
        <div><strong>ETD:</strong> {form.etd}</div>
        <div><strong>ETA:</strong> {form.eta}</div>
        <div><strong>Descrição:</strong> {form.descricao}</div>
        <div><strong>Grupos:</strong> {form.groups.map(group => group.label).join(', ')}</div>
      </SpaceBetween>
    </Box>
  );

  // Handle POST submit
  async function handleSubmit() {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const accessToken = localStorage.getItem('access_token');
      if(stage === 'dev') console.log('[DEBUG] This is the access token in CriarProcesso: '+accessToken);

      // Extract just the group IDs for the API
      const groupIds = form.groups.map(group => group.value);

      const res = await fetch('https://jbc540hz12.execute-api.us-east-2.amazonaws.com/imports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': accessToken
        },
        body: JSON.stringify({
          ...form,
          groups: groupIds // Send just the group IDs to the API
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Erro ao criar processo.");
      }
      setSuccess('Processo criado com sucesso!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.message || "Erro desconhecido ao criar processo.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ContentLayout>
        <Container header={<Header variant="h1">Criar Processo</Header>}>
          {error && <Alert type="error">{error}</Alert>}
          {success && <Alert type="success">{success}</Alert>}
          <Wizard
            activeStepIndex={activeStep}
            onNavigate={({ detail }) => setActiveStep(detail.requestedStepIndex)}
            steps={[
              {
                title: "Informações do Processo",
                content: inputStep,
                isOptional: false
              },
              {
                title: "Revisão e Envio",
                content: reviewStep,
                isOptional: false
              }
            ]}
            i18nStrings={{
              stepNumberLabel: stepNumber => `Etapa ${stepNumber}`,
              collapsedStepsLabel: (stepNumber, stepsCount) => `Etapas ${stepNumber} de ${stepsCount}`,
              skipToButtonLabel: (step, stepNumber) => `Pular para ${step.title}`,
              navigationAriaLabel: "Etapas",
              cancelButton: "Cancelar",
              previousButton: "Anterior",
              nextButton: activeStep === 0 ? "Próximo" : "Enviar",
              submitButton: "Enviar"
            }}
            onSubmit={handleSubmit}
            isLoadingNextStep={submitting}
          />
        </Container>
    </ContentLayout>
  );
}
